import React, { useCallback, useEffect, useRef, useState } from 'react';
import { translateText } from '../../lib/aiTranslate';
import { sb } from '../../supabase';
import ProductRefCard from '../product/ProductRefCard';

const LANG_NAMES = { ar: 'العربية', en: 'English', zh: '中文' };

// aiTranslate detects the source and persists to localStorage; we also persist to
// factory_message_translations (DB) so a line is translated once and reused
// cross-device/session — full parity with the direct chat.
async function translateChatLine(text, target) {
  if (!text || target === 'off') return null;
  const { translated, sourceLang, error } = await translateText(text, target);
  if (error || sourceLang === target) return null;
  return { translated, source: sourceLang };
}

// Shared conversation UI for factory threads, used by both sides:
//  • trader view (selfRole="trader") — loads/sends via the base tables
//  • factory view (selfRole="factory") — loads/sends via the masked RPCs
// It owns all chat behaviour (load, 5s poll, optimistic send, autoscroll); the
// parent supplies the data-source functions and header. Purely counterpart-masked:
// bubbles carry no sender names, only the header identifies the other party.
const S = {
  ar: { ph: 'اكتب رسالتك…', send: 'إرسال', loading: 'جارٍ التحميل…', back: 'رجوع', chinaTime: 'توقيت الصين', translating: 'جارٍ الترجمة…', transOff: 'بدون ترجمة', transTitle: 'ترجمة الرسائل', archived: 'هذه محادثة مؤرشفة للقراءة فقط. المحادثات الجديدة تتم من صفحة المصنع.' },
  en: { ph: 'Type your message…', send: 'Send', loading: 'Loading…', back: 'Back', chinaTime: 'China time', translating: 'Translating…', transOff: 'No translation', transTitle: 'Translate messages', archived: 'This conversation is archived and read-only. New conversations happen on the factory page.' },
  zh: { ph: '输入您的消息…', send: '发送', loading: '加载中…', back: '返回', chinaTime: '中国时间', translating: '翻译中…', transOff: '不翻译', transTitle: '翻译消息', archived: '此对话已归档，仅供查看。新对话请在工厂页面进行。' },
};

const CSS = `
  .ftc-page { max-width: 760px; margin: 0 auto; display: flex; flex-direction: column; height: var(--app-dvh); }
  .ftc-head { display: flex; align-items: center; gap: 12px; padding: calc(14px + env(safe-area-inset-top)) 18px 14px; border-bottom: 1px solid rgba(0,0,0,0.08); background: #fff; position: sticky; top: 0; z-index: 5; }
  .ftc-back { background: none; border: none; cursor: pointer; color: rgba(0,0,0,0.5); font-size: 13px; font-family: var(--font-sans); padding: 4px; flex-shrink: 0; }
  .ftc-ava { width: 40px; height: 40px; border-radius: 10px; object-fit: cover; background: #efe9df; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-weight: 600; color: #8B7355; font-family: var(--font-sans); }
  .ftc-name { font-size: 15px; font-weight: 600; color: rgba(0,0,0,0.86); margin: 0; line-height: 1.3; }
  .ftc-meta { font-size: 12px; color: rgba(0,0,0,0.45); margin: 0; }
  .ftc-hx { flex-shrink: 0; }
  .ftc-trans { margin-inline-start: auto; flex-shrink: 0; font-size: 12.5px; font-family: var(--font-sans); padding: 6px 8px; border-radius: 8px; border: 1px solid rgba(0,0,0,0.14); background: #fff; color: rgba(0,0,0,0.7); cursor: pointer; max-width: 150px; }
  .ftc-scroll { flex: 1; overflow-y: auto; padding: 20px 18px; display: flex; flex-direction: column; gap: 10px; background: var(--bg-base, #faf8f4); }
  .ftc-empty { margin: auto; color: rgba(0,0,0,0.4); font-size: 14px; text-align: center; max-width: 320px; line-height: 1.6; }
  .ftc-row { display: flex; flex-direction: column; max-width: 78%; }
  .ftc-row.me { align-self: flex-end; align-items: flex-end; }
  .ftc-row.them { align-self: flex-start; align-items: flex-start; }
  .ftc-row.sys { align-self: center; align-items: center; max-width: 90%; }
  .ftc-bubble { padding: 9px 13px; border-radius: 14px; font-size: 14px; line-height: 1.55; white-space: pre-wrap; word-break: break-word; font-family: var(--font-sans); }
  .ftc-row.me .ftc-bubble { background: #1a1814; color: #fff; border-bottom-right-radius: 5px; }
  .ftc-row.them .ftc-bubble { background: #fff; color: rgba(0,0,0,0.85); border: 1px solid rgba(0,0,0,0.08); border-bottom-left-radius: 5px; }
  .ftc-row.sys .ftc-bubble { background: #f2ede3; color: #8a7a5f; font-size: 12.5px; border-radius: 10px; }
  .ftc-time { font-size: 10.5px; color: rgba(0,0,0,0.35); margin: 3px 4px 0; font-family: var(--font-sans); }
  .ftc-xlate { font-size: 12.5px; color: rgba(0,0,0,0.5); font-style: italic; margin: 4px 4px 0; line-height: 1.5; white-space: pre-wrap; word-break: break-word; font-family: var(--font-sans); }
  .ftc-composer { display: flex; gap: 10px; padding: 12px 16px calc(12px + env(safe-area-inset-bottom)); border-top: 1px solid rgba(0,0,0,0.08); background: #fff; align-items: flex-end; }
  .ftc-input { flex: 1; resize: none; border: 1px solid rgba(0,0,0,0.16); border-radius: 12px; padding: 10px 14px; font-size: 16px; font-family: inherit; max-height: 120px; outline: none; line-height: 1.5; }
  .ftc-input:focus { border-color: rgba(0,0,0,0.4); }
  .ftc-send { background: #1a1814; color: #fff; border: none; border-radius: 12px; padding: 0 20px; height: 42px; font-size: 14px; font-weight: 600; cursor: pointer; font-family: var(--font-sans); flex-shrink: 0; }
  .ftc-send:disabled { opacity: 0.5; cursor: default; }
  .ftc-archived { padding: 14px 16px; text-align: center; font-size: 12.5px; line-height: 1.6; color: rgba(0,0,0,0.45); font-family: var(--font-sans); border-top: 1px solid rgba(0,0,0,0.07); }
  /* The .ftc-pref* rules moved into components/product/ProductRefCard.jsx as
     inline styles when that card was shared with Chat.jsx, which does not
     inject this stylesheet. */
  .ftc-pending { display: flex; align-items: center; gap: 8px; padding: 8px 16px 0; }
  .ftc-pending-x { background: rgba(0,0,0,0.07); border: none; width: 26px; height: 26px; border-radius: 50%; cursor: pointer; font-size: 16px; line-height: 1; color: rgba(0,0,0,0.5); flex-shrink: 0; }
`;

function fmtTime(iso, lang) {
  try {
    return new Date(iso).toLocaleString(lang === 'ar' ? 'ar' : lang === 'zh' ? 'zh' : 'en', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

export default function ThreadChat({
  lang = 'ar', selfRole, header = {}, emptyText = '', onBack, headerExtra = null,
  loadMessages, sendMessage, pollMs = 5000, pendingProduct = null, showChinaTime = false,
  onHeaderClick = null,
  // Archive mode: render the history but no composer. The legacy factory_threads
  // stack is read-only from 2026-09-06 — everything new lives in `messages`.
  readOnly = false,
  // The translation cache table (same columns: message_id, direction, translated_text);
  // «اسأل المورد» keeps its own (market_inquiry_translations).
  translationTable = 'factory_message_translations',
  // Shown above the messages, always (a pinned product card, a notice).
  pinned = null,
}) {
  const isAr = lang === 'ar';
  const s = S[lang] || S.ar;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(pendingProduct || null);   // product attached to the next message
  const [chinaTime, setChinaTime] = useState('');
  // Translation — 'off' | 'ar' | 'en' | 'zh', defaults ON to the reader's language.
  const [transTarget, setTransTarget] = useState(lang);
  const [translations, setTranslations] = useState({});   // { `${target}:${msgId}`: text }
  const [pendingTrans, setPendingTrans] = useState({});   // { cacheKey: true } in-flight
  const bottomRef = useRef(null);

  // Live China clock in the header — factories are in China, so the trader sees
  // the counterpart's local time (mirrors the direct supplier chat). Opt-in via
  // showChinaTime so the factory-side view never labels the buyer's time.
  useEffect(() => {
    if (!showChinaTime) return undefined;
    const update = () => setChinaTime(new Date().toLocaleTimeString('en', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit' }));
    update();
    const timer = setInterval(update, 30000);
    return () => clearInterval(timer);
  }, [showChinaTime]);

  // Translation — DB cache first (factory_message_translations), then translate
  // only the still-missing incoming messages and persist them, so each line is
  // translated by the AI exactly once. Mirrors the direct chat.
  useEffect(() => {
    if (transTarget === 'off' || !messages.length) return undefined;
    let cancelled = false;

    (async () => {
      const incoming = messages.filter((m) =>
        m.sender_role !== selfRole && m.sender_role !== 'admin' && (m.content || '').trim());
      if (!incoming.length) return;

      const dbHits = {};
      const realIds = incoming.filter((m) => !String(m.id).startsWith('tmp-')).map((m) => m.id);
      if (realIds.length) {
        const { data } = await sb.from(translationTable)
          .select('message_id, translated_text')
          .like('direction', `%_to_${transTarget}`)
          .in('message_id', realIds);
        (data || []).forEach((row) => { dbHits[`${transTarget}:${row.message_id}`] = row.translated_text; });
        if (!cancelled && Object.keys(dbHits).length) setTranslations((prev) => ({ ...prev, ...dbHits }));
      }
      if (cancelled) return;

      incoming.forEach((msg) => {
        const cacheKey = `${transTarget}:${msg.id}`;
        if (translations[cacheKey] || dbHits[cacheKey] || pendingTrans[cacheKey]) return;
        setPendingTrans((prev) => ({ ...prev, [cacheKey]: true }));
        translateChatLine(msg.content, transTarget).then((result) => {
          if (cancelled) return;
          setPendingTrans((prev) => { const n = { ...prev }; delete n[cacheKey]; return n; });
          if (!result) return;
          setTranslations((prev) => ({ ...prev, [cacheKey]: result.translated }));
          if (!String(msg.id).startsWith('tmp-')) {
            sb.from(translationTable).upsert({
              message_id: msg.id,
              direction: `${result.source}_to_${transTarget}`,
              translated_text: result.translated,
            }, { onConflict: 'message_id,direction' }).then(() => {}, () => {});
          }
        });
      });
    })();

    return () => { cancelled = true; };
  }, [transTarget, messages.length, selfRole]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = useCallback(async () => {
    try { const m = await loadMessages(); setMessages(m); }
    catch { /* transient poll error — keep last state */ }
  }, [loadMessages]);

  useEffect(() => {
    let alive = true;
    (async () => { await refresh(); if (alive) setLoading(false); })();
    return () => { alive = false; };
  }, [refresh]);

  useEffect(() => {
    const iv = setInterval(refresh, pollMs);
    return () => clearInterval(iv);
  }, [refresh, pollMs]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length]);

  async function send() {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    const ref = pending;   // attach the pending product to this (first) message
    const optimistic = { id: `tmp-${Date.now()}`, sender_role: selfRole, content: body, product_ref: ref || null, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setText('');
    try { await sendMessage(body, ref || null); if (ref) setPending(null); await refresh(); }
    catch { setMessages((m) => m.filter((x) => x.id !== optimistic.id)); setText(body); }
    setSending(false);
  }

  function onKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  }

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
      <style>{CSS}</style>
      <div className="ftc-page">
        <div className="ftc-head">
          {onBack && <button className="ftc-back" onClick={onBack}>{isAr ? '→' : '←'} {s.back}</button>}
          {header.avatar
            ? <img className="ftc-ava" src={header.avatar} alt="" />
            : <div className="ftc-ava">{(header.name || '?')[0]}</div>}
          <div onClick={onHeaderClick || undefined} style={onHeaderClick ? { cursor: 'pointer' } : undefined}>
            <p className="ftc-name">
              {header.name}
              {onHeaderClick && <span style={{ fontSize: 11, marginInlineStart: 6, color: 'rgba(0,0,0,0.4)' }}>↗</span>}
            </p>
            {header.meta && <p className="ftc-meta">{header.meta}</p>}
            {showChinaTime && chinaTime && <p className="ftc-meta">{chinaTime} · {s.chinaTime}</p>}
          </div>
          <select className="ftc-trans" value={transTarget} title={s.transTitle} aria-label={s.transTitle}
            onChange={(e) => setTransTarget(e.target.value)}>
            <option value="off">🌐 {s.transOff}</option>
            {['ar', 'en', 'zh'].map((k) => <option key={k} value={k}>🌐 {LANG_NAMES[k]}</option>)}
          </select>
          {headerExtra && <div className="ftc-hx">{headerExtra}</div>}
        </div>

        <div className="ftc-scroll">
          {pinned}
          {loading ? <p className="ftc-empty">{s.loading}</p>
            : messages.length === 0 ? <p className="ftc-empty">{emptyText}</p>
              : messages.map((m) => {
                const role = m.sender_role === selfRole ? 'me' : m.sender_role === 'admin' ? 'sys' : 'them';
                const cacheKey = `${transTarget}:${m.id}`;
                const translated = (role === 'them' && m.content && transTarget !== 'off') ? translations[cacheKey] : null;
                const translating = (role === 'them' && m.content && transTarget !== 'off' && !translated) ? pendingTrans[cacheKey] : false;
                return (
                  <div className={`ftc-row ${role}`} key={m.id}>
                    {/* onDark replaces the old `.ftc-row.me .ftc-pref` cascade,
                        which the shared card cannot see. */}
                    {m.product_ref && <ProductRefCard pr={m.product_ref} lang={lang} onDark={role === 'me'} viewerIsSupplier={selfRole === 'factory'} />}
                    {m.content ? <div className="ftc-bubble">{m.content}</div> : null}
                    {(translated || translating)
                      ? <div className="ftc-xlate">{translating ? s.translating : translated}</div>
                      : null}
                    <span className="ftc-time">{fmtTime(m.created_at, lang)}</span>
                  </div>
                );
              })}
          <div ref={bottomRef} />
        </div>

        {!readOnly && pending && (
          <div className="ftc-pending">
            {/* was `.ftc-pending .ftc-pref { margin: 0; flex: 1 }` */}
            <ProductRefCard pr={pending} lang={lang} link={false} style={{ margin: 0, flex: 1 }} viewerIsSupplier={selfRole === 'factory'} />
            <button type="button" className="ftc-pending-x" onClick={() => setPending(null)} aria-label={isAr ? 'إزالة' : 'Remove'}>×</button>
          </div>
        )}
        {readOnly ? (
          <p className="ftc-archived">{s.archived}</p>
        ) : (
          <div className="ftc-composer">
            <textarea className="ftc-input" rows={1} value={text} placeholder={s.ph}
              onChange={(e) => setText(e.target.value)} onKeyDown={onKey} dir={isAr ? 'rtl' : 'ltr'} />
            <button className="ftc-send" onClick={send} disabled={sending || !text.trim()}>{s.send}</button>
          </div>
        )}
      </div>
    </div>
  );
}
